/**
 * Size Converter - Inches to CM for Tapestry Sizes
 * Automatically detects size variants and adds conversion tooltips
 */

class SizeConverter {
  constructor() {
    this.inchesToCm = 2.54;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.addConverterTooltips());
    } else {
      this.addConverterTooltips();
    }

    // Re-run when product changes (for quick shop, variant changes, etc.)
    document.addEventListener('variant:change', () => this.addConverterTooltips());
  }

  /**
   * Detect if a string contains size information in inches
   * Supports formats like: "24x36", "24 x 36", "24"x36"", "24in x 36in"
   */
  detectSizeInInches(text) {
    // Remove quotes and clean up
    const cleanText = text.replace(/["']/g, '').trim();

    // Match patterns like: 24x36, 24 x 36, 24in x 36in, etc.
    const patterns = [
      /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)/,  // 24x36 or 24 x 36
      /(\d+(?:\.\d+)?)\s*(?:in|inch|inches|"|'')\s*[xX×]\s*(\d+(?:\.\d+)?)/,  // 24in x 36in
      /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)\s*(?:in|inch|inches|"|'')/   // 24 x 36in
    ];

    for (const pattern of patterns) {
      const match = cleanText.match(pattern);
      if (match) {
        return {
          width: parseFloat(match[1]),
          height: parseFloat(match[2])
        };
      }
    }

    // Single dimension (less common)
    const singleMatch = cleanText.match(/(\d+(?:\.\d+)?)\s*(?:in|inch|inches|"|'')/);
    if (singleMatch) {
      const size = parseFloat(singleMatch[1]);
      return { width: size, height: size };
    }

    return null;
  }

  /**
   * Convert inches to centimeters and format nicely
   */
  convertToCm(inches) {
    const cm = (inches * this.inchesToCm).toFixed(1);
    // Remove .0 if it's a whole number
    return cm.endsWith('.0') ? cm.slice(0, -2) : cm;
  }

  /**
   * Create tooltip element
   */
  createTooltip(widthInches, heightInches) {
    const widthCm = this.convertToCm(widthInches);
    const heightCm = this.convertToCm(heightInches);

    const tooltip = document.createElement('span');
    tooltip.className = 'size-converter-tooltip';
    tooltip.textContent = `${widthCm} × ${heightCm} cm`;

    return tooltip;
  }

  /**
   * Check if variant option is likely a size option
   */
  isSizeOption(legendText) {
    const sizeKeywords = ['size', 'dimension', 'dimensions', 'measurement'];
    const lowerText = legendText.toLowerCase();
    return sizeKeywords.some(keyword => lowerText.includes(keyword));
  }

  /**
   * Add converter tooltips to all size variant options
   */
  addConverterTooltips() {
    // Find all variant option fieldsets
    const variantFieldsets = document.querySelectorAll('.variant-option');

    variantFieldsets.forEach(fieldset => {
      const legend = fieldset.querySelector('legend');
      if (!legend) return;

      // Check if this is a size-related option
      const legendText = legend.textContent.trim();
      if (!this.isSizeOption(legendText)) return;

      // Find all variant buttons in this fieldset
      const labels = fieldset.querySelectorAll('.variant-option__button-label');

      labels.forEach(label => {
        // Skip if tooltip already exists
        if (label.querySelector('.size-converter-tooltip')) return;

        // Get the variant text
        const textElement = label.querySelector('.variant-option__button-label__text');
        if (!textElement) return;

        const variantText = textElement.textContent.trim();

        // Try to detect size in inches
        const sizeData = this.detectSizeInInches(variantText);

        if (sizeData) {
          // Create and append tooltip
          const tooltip = this.createTooltip(sizeData.width, sizeData.height);
          label.appendChild(tooltip);
        }
      });
    });
  }
}

// Initialize the size converter
const sizeConverter = new SizeConverter();

// Export for potential external use
if (typeof window !== 'undefined') {
  window.SizeConverter = SizeConverter;
  window.sizeConverter = sizeConverter;
}
